import React, { useState, useEffect } from 'react';
import { Heart, X, Home } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { useNavigate } from 'react-router-dom';

type Ministry = Database['public']['Tables']['ministries']['Row'];

interface JoinMinistryModalProps {
  isOpen: boolean;
  onClose: () => void;
  ministry: Ministry;
}

interface LeadershipModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function JoinMinistryModal({ isOpen, onClose, ministry }: JoinMinistryModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [experience, setExperience] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Aqui você pode implementar a lógica de envio
      // Por exemplo, enviar um email para o líder do ministério
      console.log('Solicitação enviada:', { name, email, phone, experience, ministry });
      alert('Sua solicitação foi enviada com sucesso! Em breve entraremos em contato.');
      onClose();
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      alert('Erro ao enviar solicitação. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative shadow-elevation-3">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-brand-teal">Participar do Ministério</h2>
          <p className="text-gray-600 mt-2">{ministry.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Nome
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Telefone
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
              Experiência Prévia
            </label>
            <textarea
              id="experience"
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal h-32 transition-shadow"
              placeholder="Conte-nos sobre sua experiência nesta área..."
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand-teal text-white py-3 px-4 rounded-xl hover:bg-brand-teal/90 transition-colors disabled:opacity-50 shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
          >
            {submitting ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        </form>
      </div>
    </div>
  );
}

function LeadershipModal({ isOpen, onClose }: LeadershipModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ministry, setMinistry] = useState('');
  const [vision, setVision] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Aqui você pode implementar a lógica de envio
      console.log('Solicitação enviada:', { name, email, phone, ministry, vision });
      alert('Sua solicitação foi enviada com sucesso! Em breve entraremos em contato.');
      onClose();
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      alert('Erro ao enviar solicitação. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative shadow-elevation-3">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-brand-teal">Quero Ser Líder</h2>
          <p className="text-gray-600 mt-2">
            Compartilhe sua visão e experiência conosco
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Nome
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Telefone
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="ministry" className="block text-sm font-medium text-gray-700 mb-1">
              Ministério de Interesse
            </label>
            <input
              type="text"
              id="ministry"
              value={ministry}
              onChange={(e) => setMinistry(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              required
            />
          </div>

          <div>
            <label htmlFor="vision" className="block text-sm font-medium text-gray-700 mb-1">
              Sua Visão
            </label>
            <textarea
              id="vision"
              value={vision}
              onChange={(e) => setVision(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal h-32 transition-shadow"
              placeholder="Compartilhe sua visão para este ministério..."
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand-teal text-white py-3 px-4 rounded-xl hover:bg-brand-teal/90 transition-colors disabled:opacity-50 shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
          >
            {submitting ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Ministries() {
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMinistry, setSelectedMinistry] = useState<Ministry | null>(null);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isLeadershipModalOpen, setIsLeadershipModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMinistries();
  }, []);

  async function fetchMinistries() {
    try {
      const { data, error } = await supabase
        .from('ministries')
        .select('*')
        .order('name');

      if (error) throw error;
      setMinistries(data || []);
    } catch (error) {
      console.error('Erro ao buscar ministérios:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleJoinMinistry = (ministry: Ministry) => {
    setSelectedMinistry(ministry);
    setIsJoinModalOpen(true);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p>Carregando ministérios...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Heart className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Ministérios</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <p className="text-gray-600 mb-8">
        Descubra onde você pode servir na casa do Senhor. Temos diversos ministérios
        onde você pode usar seus dons e talentos para glorificar a Deus.
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        {ministries.map((ministry) => (
          <div key={ministry.id} className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
            <h3 className="text-xl font-semibold text-brand-teal mb-2">{ministry.name}</h3>
            <p className="text-gray-600 mb-4">{ministry.description}</p>
            {ministry.requirements && (
              <div className="mb-4">
                <strong className="text-brand-teal">Requisitos:</strong>
                <p className="text-gray-600">{ministry.requirements}</p>
              </div>
            )}
            <button
              onClick={() => handleJoinMinistry(ministry)}
              className="w-full bg-brand-teal text-white px-6 py-3 rounded-xl hover:bg-brand-teal/90 transition-colors shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
            >
              Quero participar
            </button>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-brand-light p-6 rounded-xl shadow-elevation-1">
        <h2 className="text-xl font-semibold text-brand-teal mb-4">Quer liderar um ministério?</h2>
        <p className="text-gray-600 mb-4">
          Se você tem o desejo de liderar um ministério, entre em contato conosco.
          Oferecemos treinamento e suporte para novos líderes.
        </p>
        <button
          onClick={() => setIsLeadershipModalOpen(true)}
          className="bg-brand-teal text-white px-6 py-3 rounded-xl hover:bg-brand-teal/90 transition-colors shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
        >
          Quero ser líder
        </button>
      </div>

      {selectedMinistry && (
        <JoinMinistryModal
          isOpen={isJoinModalOpen}
          onClose={() => setIsJoinModalOpen(false)}
          ministry={selectedMinistry}
        />
      )}

      <LeadershipModal
        isOpen={isLeadershipModalOpen}
        onClose={() => setIsLeadershipModalOpen(false)}
      />
    </div>
  );
}